import axios from 'axios';
import {VUE_APP_APIPATH} from './globals';
import store from '../store'
import ListData from "@/model/list_data";

const getRange = (limit, offset) => {
  return `[${offset},${offset + limit - 1}]`;
}

function ensureArrayItems(raw) {
  if (Array.isArray(raw)) return raw;
  if (!raw) return [];
  if (Array.isArray(raw.items)) return raw.items;
  if (Array.isArray(raw.data)) return raw.data;
  if (raw.data && Array.isArray(raw.data.items)) return raw.data.items;
  return [];
}

const getNotifications = async (limit = 8, offset = 0, {unread_only = false} = {}) => {
  const url = VUE_APP_APIPATH + 'model/notifications';
  const params = {range: getRange(limit, offset)};
  if (unread_only) params.unread_only = '1';
  const response = await axios.get(url, {
    params,
    headers: await store.dispatch('getAuthHeader'),
  });
  let headers = response.headers
  let total = parseInt(headers['x-total-count']);
  return new ListData(total, response.data);
};

// 點開單一通知時標記已讀（不影響其他未讀通知）
const markNotificationRead = async (id) => {
  const url = VUE_APP_APIPATH + `model/notifications/${id}/read`;
  await axios.post(url, {}, {
    headers: await store.dispatch('getAuthHeader'),
  });
};

const getUnreadNotificationCount = async () => {
  const url = VUE_APP_APIPATH + 'model/notifications/unread_count';
  const response = await axios.get(url, {
    headers: await store.dispatch('getAuthHeader'),
  });
  return response.data.count;
};

// 推播裝置（FCM token）登記／取消登記
const registerPushToken = async (token) => {
  const url = VUE_APP_APIPATH + 'model/message_tokens';
  await axios.post(url, {token, platform: 'web'}, {
    headers: await store.dispatch('getAuthHeader'),
  });
};

const unregisterPushToken = async (token) => {
  const url = VUE_APP_APIPATH + 'model/message_tokens/unregister';
  await axios.post(url, {token}, {
    headers: await store.dispatch('getAuthHeader'),
  });
};

const markNotificationsRead = async () => {
  const url = VUE_APP_APIPATH + 'model/notifications/mark_read';
  await axios.post(url, {}, {
    headers: await store.dispatch('getAuthHeader'),
  });
};

const getNews = async (limit = 8, offset = 0) => {
  const url = VUE_APP_APIPATH + 'model/news';
  const response = await axios.get(url, {
    params: {range: getRange(limit, offset), sort: JSON.stringify([['publish_date', 'DESC']])},
    headers: await store.dispatch('getAuthHeader'),
  });
  let headers = response.headers
  let total = parseInt(headers['x-total-count']);
  return new ListData(total, response.data);
};

const getTopRanks = async (limit = 5, offset = 0) => {
  const url = VUE_APP_APIPATH + 'model/top_ranks';
  const response = await axios.get(url, {
    params: {range: getRange(limit, offset)},
    headers: await store.dispatch('getAuthHeader'),
  });
  let headers = response.headers
  let total = parseInt(headers['x-total-count']);
  return new ListData(total, response.data);
};

const getBrands = async (limit = 10, offset = 0) => {
  const url = VUE_APP_APIPATH + 'model/commodity_brands';
  const response = await axios.get(url, {
    params: {range: getRange(limit, offset), sort: JSON.stringify([['sort_order', 'ASC']])},
    headers: await store.dispatch('getAuthHeader'),
  });
  let headers = response.headers
  let total = parseInt(headers['x-total-count']);
  return new ListData(total, response.data);
};

const getCatalogs = async (listOptions = 10, offset = 0, sort) => {
  // Support two calling styles:
  // - getCatalogs(limit, offset, sort)
  // - getCatalogs({ limit, offset, sorts, filters, params })
  let opts = {};
  if (typeof listOptions === 'object' && listOptions !== null) {
    opts = listOptions;
  } else {
    opts = {
      limit: listOptions,
      offset: offset,
      sorts: Array.isArray(sort) ? sort : undefined,
    };
  }

  const url = VUE_APP_APIPATH + 'model/catalogs';
  const params = Object.assign({}, opts.params || {});
  // ensure defaults
  if (typeof params.commodity === 'undefined') params.commodity = '0';
  if (typeof params.tag === 'undefined') params.tag = '1';

  const limit = opts.limit !== undefined ? opts.limit : 10;
  const off = opts.offset !== undefined ? opts.offset : 0;
  params.range = getRange(limit, off);

  // sorts: accept either array of [col, dir] or array of {column,direction}
  if (opts.sorts && Array.isArray(opts.sorts) && opts.sorts.length > 0) {
    function normalizeSort(s) {
      // tolerate many shapes; never throw
      try {
        // string form: 'publish_date' -> default DESC
        if (typeof s === 'string') {
          return [s, 'DESC'];
        }

        // array form: ['col', 'DESC'] or ['col']
        if (Array.isArray(s)) {
          const col = s[0] !== undefined ? s[0] : String(s);
          const dir = s[1] ? String(s[1]).toUpperCase() : 'DESC';
          return [col, dir];
        }

        // object form: { column: 'col', direction: 'desc' } or { column: 'col', order: 'DESC' }
        if (s && typeof s === 'object') {
          const col = s.column || s.field || s[0] || Object.keys(s).find(k => typeof s[k] === 'string') || String(s);
          const dir = s.direction || s.order || s.dir || 'DESC';
          return [col, String(dir).toUpperCase()];
        }

        // fallback: stringify
        return [String(s), 'DESC'];
      } catch (err) {
        // never throw to avoid breaking callers; fallback to stringified form
        return [String(s), 'DESC'];
      }
    }
    params.sort = JSON.stringify(opts.sorts.map(normalizeSort));
  }

  // filters: accept object (key: value) or array of {column,type,value}
  if (opts.filters) {
    if (Array.isArray(opts.filters)) {
      const filterObj = {};
      for (let f of opts.filters) {
        const col = f.column === 'q' ? 'q' : `${f.column}.${f.type}`;
        filterObj[col] = (typeof f.value === 'boolean') ? (f.value ? '1' : '0') : f.value;
      }
      params.filter = JSON.stringify(filterObj);
    } else if (typeof opts.filters === 'object') {
      params.filter = JSON.stringify(opts.filters);
    }
  }

  const response = await axios.get(url, {
    params: params,
    headers: await store.dispatch('getAuthHeader'),
  });
  let headers = response.headers
  let total = parseInt(headers['x-total-count']);
  if (isNaN(total)) total = undefined;
  const raw = response && response.data ? response.data : null;
  const items = ensureArrayItems(raw);
  if (!total) total = items.length;
  return new ListData(total, items);
};

const getHistoryCatalogs = async ({ has_orders = false, keyword = '', dateField, dateValue, limit = 24, offset = 0 } = {}) => {
  const url = VUE_APP_APIPATH + 'model/catalogs';
  const filter = { has_orders };
  if (keyword) filter.q = keyword;
  // dateField 是 'publish_date' 或 'final_date'，dateValue 是日期選擇器給的 'YYYY-MM-DD'；
  // 用整天範圍比對，才不會因為欄位存的是 DATETIME（含時分秒）而篩不到當天資料。
  //
  // 時間字串一定要明確帶上台灣時區（+08:00）：使用者選的「某一天」指的是台灣的
  // 那一天，而資料庫的值等同 UTC（Sequelize 連線 timezone 預設 +00:00）。
  // 若送不帶時區的字串，後端 new Date() 會依伺服器所在時區解析，換到別台機器
  // （例如 UTC 的容器）結果就會整個位移，篩選時好時壞。
  if (dateField && dateValue) {
    filter[`${dateField}.gte`] = `${dateValue}T00:00:00+08:00`;
    filter[`${dateField}.lte`] = `${dateValue}T23:59:59+08:00`;
  }
  const response = await axios.get(url, {
    params: {
      pre_order: '1',
      filter: JSON.stringify(filter),
      range: getRange(limit, offset),
    },
    headers: await store.dispatch('getAuthHeader'),
  });
  const raw = response && response.data ? response.data : null;
  const items = ensureArrayItems(raw);
  let total = parseInt((response.headers || {})['x-total-count']);
  if (isNaN(total)) total = items.length;
  return new ListData(total, items);
};

// 歷史訂單頁的日期選擇器只能選這家店真的有訂單的那幾天，依 dateField
// （'publish_date' 或 'final_date'）各自查去重後的日期清單，由新到舊排序。
const getHistoryDates = async (dateField) => {
  const url = VUE_APP_APIPATH + 'model/catalogs/history_dates';
  const response = await axios.get(url, {
    params: {date_field: dateField},
    headers: await store.dispatch('getAuthHeader'),
  });
  const data = response && response.data ? response.data : {};
  return Array.isArray(data.dates) ? data.dates : [];
};

// 最新訂單頁的日期選擇器用：未結單型錄的發布日／截止日清單（由新到舊）
// 過往訂單的日期清單：已結單型錄的截止日（由新到舊）
const getPastDates = async (dateField) => {
  const url = VUE_APP_APIPATH + 'model/catalogs/past_dates';
  const response = await axios.get(url, {
    params: {date_field: dateField},
    headers: await store.dispatch('getAuthHeader'),
  });
  const data = response && response.data ? response.data : {};
  return Array.isArray(data.dates) ? data.dates : [];
};

const getLatestDates = async (dateField) => {
  const url = VUE_APP_APIPATH + 'model/catalogs/latest_dates';
  const response = await axios.get(url, {
    params: {date_field: dateField},
    headers: await store.dispatch('getAuthHeader'),
  });
  const data = response && response.data ? response.data : {};
  return Array.isArray(data.dates) ? data.dates : [];
};

const getCatalog = async (id) => {
  const url = VUE_APP_APIPATH + 'model/catalogs/' + id;
  const response = await axios.get(url, {
    params: {commodity: '0', tag: '1'},
    headers: await store.dispatch('getAuthHeader'),
  });
  return response.data;
};


const getCommodities = async ({limit = 24, offset = 0, catalog_id}) => {
  const url = VUE_APP_APIPATH + 'model/commodities';
  const params = {range: getRange(limit, offset), sort: JSON.stringify([['serial_id', 'ASC'], ['id', 'ASC']])};
  if (catalog_id !== undefined) {
    let filter = {}
    filter['catalog_id.include'] = catalog_id;
    filter['organization_id.include'] = store.state.myOrganization.id;
    params['filter'] = JSON.stringify(filter);
  }
  const response = await axios.get(url, {
    params: params,
    headers: await store.dispatch('getAuthHeader'),
  });
  let headers = response.headers
  let total = parseInt(headers['x-total-count']);
  if (isNaN(total)) total = undefined;
  const raw = response && response.data ? response.data : null;
  const items = ensureArrayItems(raw);
  if (!total) total = items.length;
  return new ListData(total, items);
};

const getTotalPrice = async ({catalog_id}) => {
  const url = VUE_APP_APIPATH + 'model/commodities';
  const filter = {
    'catalog_id.include': catalog_id,
    'organization_id.include': store.state.myOrganization.id,
  };
  const response = await axios.get(url, {
    params: {filter: JSON.stringify(filter), total_price: 1},
    headers: await store.dispatch('getAuthHeader'),
  });
  return (response.data && response.data.total_price) || 0;
};

const updateOrderCommodity = async (
  {
    catalog_id,
    commodity_id,
    operate_amount,
    is_combine_buy,
    combine_buy_amount,
    remark,
  }
) => {
  const url = VUE_APP_APIPATH + 'model/pre_order_commodities';
  const body = {
    catalog_id: catalog_id,
    commodity_id: commodity_id,
    amount: operate_amount,
    is_combine_buy: is_combine_buy,
    combine_buy_amount: combine_buy_amount,
    organization_id: store.state.myOrganization.id,
    // organization_id: 1,
  };
  // 商品備註只在有傳入時才送，避免更新數量時誤把備註清掉
  if (remark !== undefined) body.remark = remark;
  const response = await axios.post(url, body, {
    headers: await store.dispatch('getAuthHeader'),
  });
  return response.data;
}

// 帳單頁的月份選擇器只能選這家店真的有帳單的月份（依帳單日期 bill_date 去重），
// 由新到舊排序。
const getCustomerBillMonths = async () => {
  const url = VUE_APP_APIPATH + 'model/customer_bills/months';
  const response = await axios.get(url, {
    headers: await store.dispatch('getAuthHeader'),
  });
  const data = response && response.data ? response.data : {};
  return Array.isArray(data.months) ? data.months : [];
};

const getCustomerBills = async ({month, limit = 8, offset = 0} = {}) => {
  const url = VUE_APP_APIPATH + 'model/customer_bills';
  const params = {
    range: getRange(limit, offset),
    sort: JSON.stringify([['bill_date', 'DESC']]),
  };
  const filter = {};
  if (month) {
    // bill_date 是純日期欄位（DATEONLY，無時間/時區資訊），直接用字串範圍篩選即可。
    const [year, mon] = month.split('-').map(Number);
    const lastDay = new Date(year, mon, 0).getDate();
    const pad = n => String(n).padStart(2, '0');
    filter['bill_date.start'] = `${month}-01`;
    filter['bill_date.end'] = `${month}-${pad(lastDay)}`;
  }
  if (Object.keys(filter).length > 0) {
    params['filter'] = JSON.stringify(filter);
  }
  const response = await axios.get(url, {
    params,
    headers: await store.dispatch('getAuthHeader'),
  });
  let total = parseInt((response.headers || {})['x-total-count']);
  const raw = response && response.data ? response.data : null;
  const items = ensureArrayItems(raw);
  if (isNaN(total)) total = items.length;
  return new ListData(total, items);
};

// 一筆帳單紀錄不管有幾個檔案，統一觸發一次下載：後端只有一個檔案就直接給該檔案，
// 多個檔案會打包成 zip；瀏覽器端用 blob 接住後模擬點擊下載連結。
const downloadCustomerBillFiles = async (id) => {
  const url = VUE_APP_APIPATH + `model/customer_bills/${id}/download`;
  const response = await axios.get(url, {
    headers: await store.dispatch('getAuthHeader'),
    responseType: 'blob',
  });
  const blob = new Blob([response.data], {type: response.headers['content-type']});
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  const disposition = response.headers['content-disposition'] || '';
  const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^;"'\n]+)/i);
  link.download = match ? decodeURIComponent(match[1]) : `customer_bill_${id}`;
  link.click();
  URL.revokeObjectURL(link.href);
};

// 登入公告：後台在 settings 關掉「前台顯示」就會回傳 message: null，
// 前端據此判斷要不要跳出公告 dialog。
const getLoginNotice = async () => {
  const url = VUE_APP_APIPATH + 'model/login_notice';
  const response = await axios.get(url, {
    headers: await store.dispatch('getAuthHeader'),
  });
  return (response.data && response.data.message) || null;
};

// 頁尾的官網／FB／IG 連結：後台把值清空或關掉「前台顯示」，對應欄位會是 null，
// AppFooter 就不會顯示該 icon。
const getFooterLinks = async () => {
  const url = VUE_APP_APIPATH + 'model/footer_links';
  const response = await axios.get(url, {
    headers: await store.dispatch('getAuthHeader'),
  });
  const data = response.data || {};
  return {
    official: data.official || null,
    facebook: data.facebook || null,
    instagram: data.instagram || null,
  };
};

const isFavoriteCatalog = async (catalogId) => {
  const url = VUE_APP_APIPATH + `model/catalogs/${catalogId}/favorite`;
  const response = await axios.get(url, {
    headers: await store.dispatch('getAuthHeader'),
  });
  return !!(response.data && response.data.is_favorited);
};

const addFavoriteCatalog = async (catalogId) => {
  const url = VUE_APP_APIPATH + `model/catalogs/${catalogId}/favorite`;
  const response = await axios.post(url, {}, {
    headers: await store.dispatch('getAuthHeader'),
  });
  return response.data;
};

const removeFavoriteCatalog = async (catalogId) => {
  const url = VUE_APP_APIPATH + `model/catalogs/${catalogId}/favorite`;
  const response = await axios.delete(url, {
    headers: await store.dispatch('getAuthHeader'),
  });
  return response.data;
};

export {
  getNews,
  getTopRanks,
  getBrands,
  getCatalogs,
  getHistoryCatalogs,
  getHistoryDates,
  getLatestDates,
  getPastDates,
  getCatalog,
  getCommodities,
  getTotalPrice,
  getNotifications,
  getUnreadNotificationCount,
  markNotificationsRead,
  markNotificationRead,
  registerPushToken,
  unregisterPushToken,
  getCustomerBills,
  getCustomerBillMonths,
  downloadCustomerBillFiles,
  getLoginNotice,
  getFooterLinks,

  updateOrderCommodity,

  isFavoriteCatalog,
  addFavoriteCatalog,
  removeFavoriteCatalog,
}
